/**
 * Theme palettes for Cobstream.
 *
 * The CSS variables defined in `global.css` are what every component reads.
 * Each preset below maps `id` (used as `data-theme="..."` on <html>) to the
 * concrete color values. The selector in the nav writes the id; CSS does the
 * rest via `:root[data-theme="..."]` overrides.
 *
 * Keep this in sync with the variable list in `global.css`.
 *
 * The special `'custom'` id means the user picked their own palette. Its
 * concrete colors live in `customPalette` on the ThemeContext and are applied
 * as inline CSS variables on <html> rather than via a CSS block. That keeps
 * the css file static — adding a new preset is still a CSS change, but custom
 * users don't need any global.css cooperation.
 */

export type PresetThemeId = 'dark' | 'light' | 'midnight' | 'sunset' | 'forest';
export type ThemeId = PresetThemeId | 'custom';

export interface Theme {
  id: ThemeId;
  label: string;
  description: string;
}

export const THEMES: readonly Theme[] = [
  { id: 'dark',     label: 'Dark',     description: 'The default. Deep blue-black with cinema red accent.' },
  { id: 'light',    label: 'Light',    description: 'Bright neutral surface with the same red accent.' },
  { id: 'midnight', label: 'Midnight', description: 'Darker still, with a violet accent.' },
  { id: 'sunset',   label: 'Sunset',   description: 'Warm browns, oranges, and amber.' },
  { id: 'forest',   label: 'Forest',   description: 'Mossy greens and bark tones.' },
] as const;

export const DEFAULT_THEME: ThemeId = 'dark';

/**
 * Shape of a user-defined palette. We only ask for four anchor colors and
 * derive the remaining CSS tokens (muted text, hover, border, gradients) from
 * those — see `paletteToCssVars` below.
 */
export interface CustomPalette {
  bg: string;
  surface: string;
  text: string;
  accent: string;
}

export const DEFAULT_CUSTOM_PALETTE: CustomPalette = {
  bg: '#0a0e1a',
  surface: '#1a2235',
  text: '#e6ecff',
  accent: '#ff3b5c',
};

/** A "custom" entry shown at the bottom of the picker. Not in THEMES because
 * its appearance in the dropdown depends on whether the user has one saved. */
export const CUSTOM_THEME_ENTRY: Theme = {
  id: 'custom',
  label: 'Custom',
  description: 'Your saved palette.',
};

export function isPresetThemeId(value: unknown): value is PresetThemeId {
  return typeof value === 'string' && THEMES.some((t) => t.id === value);
}

export function isThemeId(value: unknown): value is ThemeId {
  return isPresetThemeId(value) || value === 'custom';
}

export function isCustomPalette(value: unknown): value is CustomPalette {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    isHexColor(v.bg) &&
    isHexColor(v.surface) &&
    isHexColor(v.text) &&
    isHexColor(v.accent)
  );
}

function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
}

/**
 * Expand the user's 4-color palette into the full set of CSS variables that
 * the rest of the app reads. Helpers below derive secondary tokens by mixing
 * the anchors with black/white in HSL so e.g. "bg-2" is just a slightly
 * lighter or darker variant.
 *
 * This intentionally does not try to produce a perfect WCAG-compliant scheme;
 * the user picked these colors, they get what they picked. The derived tokens
 * just need to be "in the same family" so surfaces don't look broken.
 */
export function paletteToCssVars(p: CustomPalette): Record<string, string> {
  const bgLight = isLight(p.bg);
  const surfaceLight = isLight(p.surface);
  const accentSoft = hexToRgba(p.accent, 0.16);
  return {
    '--bg': p.bg,
    '--bg-2': shade(p.bg, bgLight ? -0.04 : 0.05),
    '--surface': p.surface,
    '--surface-2': shade(p.surface, surfaceLight ? -0.05 : 0.07),
    '--text': p.text,
    '--text-muted': mix(p.text, p.bg, 0.45),
    '--text-dim': mix(p.text, p.bg, 0.65),
    '--accent': p.accent,
    '--accent-hover': shade(p.accent, 0.1),
    '--accent-soft': accentSoft,
    '--on-accent': isLight(p.accent) ? '#0a0e1a' : '#ffffff',
    '--danger': '#ff6b6b',
    '--border': mix(p.surface, p.text, 0.18),
    '--body-gradient': `radial-gradient(1200px 600px at 70% -10%, ${hexToRgba(p.accent, 0.18)}, transparent 60%), linear-gradient(180deg, ${p.bg} 0%, ${shade(p.bg, bgLight ? -0.02 : 0.03)} 100%)`,
    '--shadow-card': bgLight
      ? '0 10px 25px rgba(20, 30, 60, 0.08)'
      : '0 20px 40px rgba(0, 0, 0, 0.3)',
  };
}

/* ---------- color helpers (pure, no DOM) ---------- */

function parseHex(hex: string): [number, number, number] {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return [r, g, b];
}

function toHex(r: number, g: number, b: number): string {
  const clamp = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
  return '#' + [clamp(r), clamp(g), clamp(b)].map((v) => v.toString(16).padStart(2, '0')).join('');
}

export function isLight(hex: string): boolean {
  const [r, g, b] = parseHex(hex);
  // Relative luminance, BT.601 approximation.
  const y = (r * 299 + g * 587 + b * 114) / 1000;
  return y > 160;
}

/** Lighten (amount > 0) or darken (amount < 0) by mixing toward white/black. */
export function shade(hex: string, amount: number): string {
  const [r, g, b] = parseHex(hex);
  const target = amount > 0 ? 255 : 0;
  const k = Math.abs(amount);
  return toHex(r + (target - r) * k, g + (target - g) * k, b + (target - b) * k);
}

/** Mix two colors: `1` returns `b`, `0` returns `a`. */
export function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = parseHex(a);
  const [br, bg, bb] = parseHex(b);
  return toHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

export function hexToRgba(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
